import { useCallback, useEffect, useState } from "react";
import { DoorClosed, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const slug = (name) => name.replace(/\W+/g, "-").toLowerCase().slice(0, 24);

// Publishers who keep MiLu out — hidden from the feed in one tap.
export const BlockedPublishers = ({ onMuted, refreshKey = 0 }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/news/blocked");
      setRows(data.rows.filter((r) => !r.muted));
    } catch (e) {
      console.error("could not load the blocked publishers", e);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (rows.length === 0) return null;

  const hide = async (source) => {
    setBusy(source);
    try {
      const { data } = await api.post("/news/mute", { source });
      setRows((r) => r.filter((x) => x.source !== source));
      toast.success(`${source} is out of your feed`);
      await onMuted?.(data.muted_sources);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not hide that publisher");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mt-4 rounded-3xl border border-white/10 bg-panel p-4" data-testid="blocked-publishers">
      <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-ink-soft">
        <DoorClosed className="h-4 w-4 text-brand" /> Never opens for MiLu
        <span className="normal-case tracking-normal">
          · {rows.length === 1 ? "one publisher" : `${rows.length} publishers`} she cannot read out
        </span>
      </p>
      <ul className="mt-2 flex flex-wrap gap-2">
        {rows.map((row) => (
          <li key={row.source}>
            <button
              data-testid={`hide-blocked-${slug(row.source)}`}
              onClick={() => hide(row.source)}
              disabled={busy === row.source}
              title={`Hide ${row.source} from my feed`}
              className="inline-flex min-h-[28px] items-center gap-1.5 rounded-full border border-white/15 px-3 text-xs text-ink-soft transition-colors hover:border-destructive/60 hover:text-destructive disabled:opacity-50"
            >
              <EyeOff className="h-3 w-3" /> {row.source}
              <span className="text-[10px] text-ink-soft/70">· blocked {row.tries}×</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
